/**
 * @module api/tags
 *
 * API functions for the episode tagging system: listing the tags visible to
 * the current user (own tags + the shared system "Star" tag), CRUD on user
 * tags (Settings management table), fetching tag→episode assignments, and
 * attaching/detaching tags on episodes.
 *
 * All functions communicate with the server via the shared Axios instance
 * from config.js and follow the standard response envelope
 * { success, data | error }.
 */

import { api } from "./config.js";

/**
 * Extracts the most useful error message from an Axios error, preferring the
 * server's structured error/message fields over the generic fallback.
 *
 * @param {*} error - The caught Axios error
 * @param {string} fallback - Message to use when the server gave none
 * @returns {Error} A plain Error carrying the resolved message and HTTP status
 */
const toError = (error, fallback) => {
  // Server error responses use { success: false, error } (sometimes message)
  const message =
    error.response?.data?.error ||
    error.response?.data?.message ||
    fallback;
  const err = new Error(message);
  // Preserve the HTTP status so callers can special-case 409 (duplicate name)
  err.status = error.response?.status;
  return err;
};

/**
 * Fetch all tags visible to the current user: system tags (e.g. "Star")
 * first, then the user's own tags alphabetically. Each tag includes an
 * episode_count for the Settings management table.
 *
 * @returns {Promise<Array>} Array of tag objects
 *   ({ id, user_id, name, is_system, episode_count, created_at })
 * @throws {Error} If the fetch fails
 */
const getTags = async () => {
  try {
    const response = await api.get("/tags");
    // Unwrap the standard { success, data } envelope
    return response.data.data;
  } catch (error) {
    throw toError(error, "Failed to load tags");
  }
};

/**
 * Fetch every (episode_id, tag_id) assignment pair for tags visible to the
 * current user. Loaded once by useTags to build the episode→tags map that
 * renders chips in the episode lists.
 *
 * @returns {Promise<Array>} Array of { episode_id, tag_id } pairs
 * @throws {Error} If the fetch fails
 */
const getTagAssignments = async () => {
  try {
    const response = await api.get("/tags/assignments");
    // Unwrap the standard { success, data } envelope
    return response.data.data;
  } catch (error) {
    throw toError(error, "Failed to load tag assignments");
  }
};

/**
 * Create a new user tag with the given name.
 *
 * @param {string} name - The tag name (server trims and validates)
 * @returns {Promise<Object>} The created tag object
 *   ({ id, user_id, name, is_system: false, episode_count: 0 })
 * @throws {Error} If creation fails (409 = duplicate name)
 */
const createTag = async (name) => {
  try {
    const response = await api.post("/tags", { name });
    // Unwrap the standard { success, data } envelope
    return response.data.data;
  } catch (error) {
    throw toError(error, `Failed to create tag "${name}"`);
  }
};

/**
 * Rename an existing user tag. System tags (Star) are rejected by the server.
 *
 * @param {number} id - The tag's database id
 * @param {string} name - The new tag name
 * @returns {Promise<Object>} The updated tag data ({ id, name })
 * @throws {Error} If the rename fails (409 = duplicate name, 403 = system tag)
 */
const updateTag = async (id, name) => {
  try {
    const response = await api.put(`/tags/${id}`, { name });
    // Unwrap the standard { success, data } envelope
    return response.data.data;
  } catch (error) {
    throw toError(error, "Failed to rename tag");
  }
};

/**
 * Delete a user tag. All of its episode assignments are removed server-side
 * via CASCADE. System tags (Star) are rejected by the server.
 *
 * @param {number} id - The tag's database id
 * @returns {Promise<Object>} Server response data
 * @throws {Error} If the deletion fails (403 = system tag)
 */
const deleteTag = async (id) => {
  try {
    const response = await api.delete(`/tags/${id}`);
    return response.data;
  } catch (error) {
    throw toError(error, "Failed to delete tag");
  }
};

/**
 * Attach a tag to an episode (idempotent). Attaching the system Star tag also
 * stars the episode server-side (episodes.starred sync).
 *
 * @param {number} tagId - The tag's database id
 * @param {number} episodeId - The episode's database id
 * @returns {Promise<Object>} Server response data
 * @throws {Error} If the attach fails
 */
const tagEpisode = async (tagId, episodeId) => {
  try {
    const response = await api.put(`/tags/${tagId}/episode/${episodeId}`);
    return response.data;
  } catch (error) {
    throw toError(error, "Failed to add tag to episode");
  }
};

/**
 * Detach a tag from an episode (idempotent). Detaching the system Star tag
 * also unstars the episode server-side (episodes.starred sync).
 *
 * @param {number} tagId - The tag's database id
 * @param {number} episodeId - The episode's database id
 * @returns {Promise<Object>} Server response data
 * @throws {Error} If the detach fails
 */
const untagEpisode = async (tagId, episodeId) => {
  try {
    const response = await api.delete(`/tags/${tagId}/episode/${episodeId}`);
    return response.data;
  } catch (error) {
    throw toError(error, "Failed to remove tag from episode");
  }
};

// =============================================================================
// Podcast tags — labels attached to a PODCAST, inherited by all its episodes
// =============================================================================

/**
 * Fetch every (podcast_id, tag_id) pair for the user's podcast labels. Used by
 * useTags to build a podcast→tags map for the podcast tag picker and chips.
 *
 * @returns {Promise<Array>} Array of { podcast_id, tag_id } pairs
 * @throws {Error} If the fetch fails
 */
const getPodcastTagAssignments = async () => {
  try {
    const response = await api.get("/podcast-tags/assignments");
    return response.data.data;
  } catch (error) {
    throw toError(error, "Failed to load podcast tag assignments");
  }
};

/**
 * Fetch every (episode_id, tag_id) pair an episode INHERITS from its podcast's
 * tags. Merged into the chips/filters so inherited labels show on episodes.
 *
 * @returns {Promise<Array>} Array of { episode_id, tag_id } pairs
 * @throws {Error} If the fetch fails
 */
const getInheritedTagAssignments = async () => {
  try {
    const response = await api.get("/tags/inherited-assignments");
    return response.data.data;
  } catch (error) {
    throw toError(error, "Failed to load inherited tag assignments");
  }
};

/**
 * Attach a tag to a podcast (idempotent). Every episode of the podcast then
 * inherits the label.
 *
 * @param {string} podcastId - The podcast id
 * @param {number} tagId - The tag's database id
 * @returns {Promise<Object>} Server response data
 * @throws {Error} If the attach fails
 */
const tagPodcast = async (podcastId, tagId) => {
  try {
    const response = await api.put(`/podcasts/${podcastId}/tags/${tagId}`);
    return response.data;
  } catch (error) {
    throw toError(error, "Failed to add tag to podcast");
  }
};

/**
 * Detach a tag from a podcast (idempotent). Its episodes stop inheriting it.
 *
 * @param {string} podcastId - The podcast id
 * @param {number} tagId - The tag's database id
 * @returns {Promise<Object>} Server response data
 * @throws {Error} If the detach fails
 */
const untagPodcast = async (podcastId, tagId) => {
  try {
    const response = await api.delete(`/podcasts/${podcastId}/tags/${tagId}`);
    return response.data;
  } catch (error) {
    throw toError(error, "Failed to remove tag from podcast");
  }
};

/**
 * Exported API object containing all tag-related functions.
 * Consumed by the useTags composable (and re-exported by api/index.js).
 */
export const tagsApi = {
  getTags,
  getTagAssignments,
  createTag,
  updateTag,
  deleteTag,
  tagEpisode,
  untagEpisode,
  // Podcast tags (inherited by a podcast's episodes)
  getPodcastTagAssignments,
  getInheritedTagAssignments,
  tagPodcast,
  untagPodcast,
};
